import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileVersion } from '~/shared/data-contract/job-profile-versions';
import { Button } from '~/ui/core/button/Button';
import {
    TableBody,
    TableCell,
    TableContainer,
    TableGrid,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '~/ui/core/table/Table';

import type { JobItemDetails } from '../../api/use-job-item-details';
import { StatusIndicator } from '../../components/StatusIndicator';
import type { useAuditTableLabels } from '../use-audit-table-labels';
import { renderUserPrimary } from './AuditActorCell';
import { AuditStamp } from './AuditStamp';
import { USER_PRIMARY_LINE_WRAPPER_CLASS } from './constants';
import { computeRowVisuals } from './row-visuals';
import type { AuditRowComputed, AuditTableStatus, AuditTrailStep } from './types';
import { VersionNoteSlot } from './VersionNoteSlot';

type AuditRowProps = {
    readonly architectureId: string;
    readonly canEditNote: boolean;
    readonly columnWidths: readonly string[];
    readonly computed: AuditRowComputed;
    readonly isArchived: boolean;
    readonly isEditingNote: boolean;
    readonly item: JobItemDetails;
    readonly noPublisherLabel: string;
    readonly note: string | undefined;
    readonly onEnterEditMode: () => void;
    readonly onExitEditMode: () => void;
    readonly onInviteReviewer?: () => void;
    readonly pendingLabel: string;
    readonly step: AuditTrailStep;
    readonly unknownUserLabel: string;
    readonly versionNumber: number;
};

const AuditRow: FunctionComponent<AuditRowProps> = ({
    architectureId,
    canEditNote,
    columnWidths,
    computed,
    isArchived,
    isEditingNote,
    item,
    noPublisherLabel,
    note,
    onEnterEditMode,
    onExitEditMode,
    onInviteReviewer,
    pendingLabel,
    step,
    unknownUserLabel,
    versionNumber,
}) => {
    const translate = useTranslate();
    const userPrimary = renderUserPrimary(
        step,
        architectureId,
        item,
        isArchived,
        onInviteReviewer,
        computed.rowTextClass,
        unknownUserLabel,
        pendingLabel,
        noPublisherLabel,
        computed.showReviewerPill,
    );

    const isDraftedRow = step.id === 'draft' && step.state !== 'upcoming';
    const hasNote = is.nonEmptyString(note);
    const showEditButton = canEditNote && isDraftedRow && hasNote && !isEditingNote;
    const rowClassName = `group !mt-xxs !rounded-none ${computed.rowBgClass}`;
    const cellClassName = '!min-h-[34px] !items-start !py-xs';

    return (
        <TableRow $columnWidths={columnWidths} className={rowClassName}>
            <TableCell className={`${cellClassName} !justify-start`} data-status-cell={true} role="gridcell">
                <StatusIndicator
                    className={computed.rowTextClass}
                    icon={<AuditStamp isNext={computed.isNextLifecycleStep} size={14} step={step} />}
                    size="md"
                >
                    {computed.stampLabel}
                </StatusIndicator>
            </TableCell>
            <TableCell className={`${cellClassName} !min-w-0 !justify-start`} role="gridcell">
                <span
                    className={`flex w-full min-w-0 justify-start ${
                        computed.noteSlotVisible ? 'flex-col items-start gap-xxs' : 'items-center'
                    }`}
                >
                    <span className={USER_PRIMARY_LINE_WRAPPER_CLASS}>{userPrimary}</span>
                    {computed.noteSlotVisible ? (
                        <VersionNoteSlot
                            architectureId={architectureId}
                            canEditNote={canEditNote}
                            isEditingNote={isEditingNote}
                            item={item}
                            note={note}
                            versionNumber={versionNumber}
                            onEnterEditMode={onEnterEditMode}
                            onExitEditMode={onExitEditMode}
                        />
                    ) : null}
                </span>
            </TableCell>
            <TableCell className={`${cellClassName} !justify-end`} role="gridcell">
                <span
                    className={`flex w-full min-w-0 flex-col items-end justify-start gap-xxs text-right ${computed.rowTextClass}`}
                >
                    <span
                        className="inline-flex min-h-[26px] max-w-full items-center truncate text-sm font-medium tracking-tight tabular-nums"
                        title={computed.whenContent}
                    >
                        {computed.whenContent}
                    </span>
                    {showEditButton ? (
                        <Button
                            className="!h-auto !p-0 text-xs font-medium !text-gray-700 hover:!text-black-900 !no-underline opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100 group-focus-within:opacity-100"
                            size="sm"
                            type="button"
                            variant="link"
                            onClick={onEnterEditMode}
                        >
                            {translate('pages.job-architecture-v2.version-note.edit')}
                        </Button>
                    ) : null}
                </span>
            </TableCell>
        </TableRow>
    );
};

export const AuditTrailTable: FunctionComponent<{
    readonly architectureId: string;
    readonly canEditNote: boolean;
    readonly columnWidths: readonly string[];
    readonly currentVersion: JobProfileVersion;
    readonly formatTimestamp: (iso: string | null) => string;
    readonly isArchived: boolean;
    readonly isEditingNote: boolean;
    readonly item: JobItemDetails;
    readonly labels: ReturnType<typeof useAuditTableLabels>;
    readonly note: string | undefined;
    readonly onEnterEditMode: () => void;
    readonly onExitEditMode: () => void;
    readonly onInviteReviewer?: () => void;
    readonly showHeader?: boolean;
    readonly status: AuditTableStatus;
    readonly steps: readonly AuditTrailStep[];
}> = ({
    architectureId,
    canEditNote,
    columnWidths,
    currentVersion,
    formatTimestamp,
    isArchived,
    isEditingNote,
    item,
    labels,
    note,
    onEnterEditMode,
    onExitEditMode,
    onInviteReviewer,
    showHeader = true,
    status,
    steps,
}) => (
    <TableContainer $inset="compact" className="!h-auto border border-gray-200">
        <TableGrid aria-label={labels.ariaLabel} role="grid">
            {showHeader ? (
                <TableHeader
                    $columnWidths={columnWidths}
                    className="!text-gray-600 !text-xxs !font-semibold !uppercase !tracking-[0.08em]"
                    role="row"
                >
                    <TableHeaderCell className="!min-h-[24px] !py-0" role="columnheader">
                        {labels.columnStatus}
                    </TableHeaderCell>
                    <TableHeaderCell className="!min-h-[24px] !py-0" role="columnheader">
                        {labels.columnUser}
                    </TableHeaderCell>
                    <TableHeaderCell className="!min-h-[24px] !justify-end !py-0" role="columnheader">
                        {labels.columnWhen}
                    </TableHeaderCell>
                </TableHeader>
            ) : null}
            <TableBody>
                {steps.map((step) => (
                    <AuditRow
                        key={step.id}
                        architectureId={architectureId}
                        canEditNote={canEditNote}
                        columnWidths={columnWidths}
                        computed={computeRowVisuals(
                            step,
                            status,
                            note,
                            canEditNote,
                            labels.pending,
                            formatTimestamp,
                            labels.labelForStep,
                        )}
                        isArchived={isArchived}
                        isEditingNote={isEditingNote}
                        item={item}
                        noPublisherLabel={labels.noPublisher}
                        note={note}
                        pendingLabel={labels.pending}
                        step={step}
                        unknownUserLabel={labels.unknownUser}
                        versionNumber={currentVersion.version}
                        onEnterEditMode={onEnterEditMode}
                        onExitEditMode={onExitEditMode}
                        onInviteReviewer={onInviteReviewer}
                    />
                ))}
            </TableBody>
        </TableGrid>
    </TableContainer>
);
